import React, { useState } from "react";
import styled from "styled-components";

import color from "constants/color";
import font from "constants/font";

//ブラウザ情報にiPhoneかiPadが含まれるかによってisIOSを判定
// (参考) https://www.nedia.ne.jp/blog/tech/website/2018/03/20/11535
const isIOS = navigator.userAgent.includes("iPhone") || navigator.userAgent.includes("iPad");

// パーツ定義
export const Wrapper = styled.div<{ width?: string }>`
  position: relative;
  width: ${({ width = "auto" }) => width};
`;
export const Input = styled.input<{
  isErrorDisplay?: boolean;
  position?: "center" | "right" | "left";
  width: string;
  height?: string;
  disabled?: boolean;
}>`
  display: block;
  text-align: ${({ position }) => position ?? "left"};
  font-size: ${isIOS ? font.size16 : font.size14};
  box-sizing: border-box;
  width: ${(props) => props.width};
  padding: 12px 16px;
  height: ${(props) => props.height ?? "48px"};
  -webkit-appearance: none;
  border: 1px solid ${({ isErrorDisplay }) => (isErrorDisplay ? color.attention : color.form.border)};
  background-color: ${({ isErrorDisplay, disabled }) =>
    isErrorDisplay ? color.attentionLight : disabled ? color.border : color.form.background};
  border-radius: 4px;
  outline: none;
  transition: 0.3s;
  z-index: 2;
  &::placeholder {
    color: ${color.text.disabled};
  }
  &:hover {
    border: 1px solid ${color.text.black};
  }
  &:focus {
    border: 1px solid ${color.text.link};
    background-color: ${color.text.linkLight};
    box-shadow: 0 0 6px rgba(0, 98, 177, 0.25);
    &::-webkit-input-placeholder {
      color: transparent;
    }
    &::-moz-placeholder {
      color: transparent;
    }
    &::-ms-input-placeholder {
      color: transparent;
    }
    &::placeholder {
      color: transparent;
    }
  }
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
    -moz-appearance: textfield;
  }
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
    -moz-appearance: textfield;
  }
  &::-webkit-clear-button {
    z-index: 1;
  }
`;
export const ErrorArea = styled.div<{
  isErrorDisplay: boolean;
  position?: "relative" | "absolute";
}>`
  display: ${({ isErrorDisplay }) => (isErrorDisplay ? "block" : "none")};
  position: ${(props) => props.position};
  white-space: nowrap;
  color: ${color.attention};
  margin: 2px 0;
  ${({ position }) =>
    position === "absolute" &&
    `
      bottom: -20px;
      left: 0;
    `}
`;
export const ErrorMessage = styled.span`
  font-size: ${font.size12};
`;

// 本体
const TextForm = ({
  type = "text",
  width = "200px",
  value = "",
  position = "left",
  placeholder = "",
  required = false,
  readOnly = false,
  onChange = () => null,
  onBlur = () => null,
  onFocus = () => null,
  isError = false,
  errorMessage = "",
  errorPosition = "absolute",
  inputMode,
  stopPropagation, // input時のeventの伝搬を防ぐ(主にshareEstimatePc版で使用)
  testId,
  disabled,
}: {
  type?: "text" | "number" | "email" | "tel" | "url";
  width?: string;
  value?: string | number;
  position?: "center" | "right" | "left";
  placeholder?: string;
  required?: boolean;
  readOnly?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  isError?: boolean;
  errorMessage?: string;
  errorPosition?: "absolute" | "relative";
  inputMode?: "text" | "numeric" | "decimal" | "tel" | "search" | "email" | "url"; // モバイルのキーボードの種類を指定
  stopPropagation?: boolean;
  testId?: string;
  disabled?: boolean;
}) => {
  const [isBlurOnce, setIsBlurOnce] = useState(false);
  // 値が空の場合はフォーカスが外れてからエラーを表示する
  const isErrorDisplay = isError && ((value === "" && isBlurOnce) || (value !== "" && isBlurOnce));

  const onBlurForm = () => {
    setIsBlurOnce(true);
    onBlur();
  };

  return (
    <Wrapper width={width}>
      <Input
        type={type}
        inputMode={inputMode}
        width={width}
        value={value}
        position={position}
        placeholder={placeholder}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlurForm}
        required={required}
        readOnly={readOnly}
        isErrorDisplay={isErrorDisplay}
        onInput={(e) => stopPropagation && e.stopPropagation()}
        data-testid={testId}
        disabled={disabled}
      />
      <ErrorArea isErrorDisplay={isErrorDisplay} position={errorPosition}>
        <ErrorMessage>{errorMessage}</ErrorMessage>
      </ErrorArea>
    </Wrapper>
  );
};

export default TextForm;
